/**
 * The wire.
 *
 * DESIGN.md section 5: JSON behind a single encode and decode module to start,
 * so it can be swapped for a packed binary layout the moment it hurts without
 * touching anything else. That swap is the reason nothing outside this file
 * ever calls JSON.stringify on a message.
 *
 * The other rule this file exists to enforce is the one that cannot be relaxed
 * for convenience:
 *
 * > Never transmit to a client any data that client is not entitled to see.
 *
 * It shows up here as a shape rather than a check. A client is told where the
 * other vehicles *are*, and never where they are *going*: `EntityState` has a
 * position and no route, and there is no message that carries another
 * vehicle's destination. That is not a precaution bolted on, it is the game.
 * The ping is the camera's location, not the car's.
 */

import type { Plate } from './plates.ts';

/**
 * Bumped when a message changes shape in a way an older client would get wrong.
 *
 * 2 adds the audit log and the optional search reason. The server refuses a
 * mismatched client outright rather than negotiating, because a half-understood
 * protocol on the enforcement side would mean a client showing a player a
 * record that is not the record.
 */
export const PROTOCOL_VERSION = 2;

/** Snapshots per second. Matches the tick rate; see TICK_HZ. */
export const SNAPSHOT_HZ = 15;

// -- client to server --------------------------------------------------------

/** Join, either as a new vehicle or reclaiming one with its key. */
export interface HelloMessage {
  t: 'hello';
  version: number;
  /** Plate of a vehicle to reclaim. Omit to be issued a new one. */
  plate?: string;
  /** The car key for that plate. DESIGN.md section 4: not a password. */
  pin?: string;
  /**
   * Ask to play the enforcement side.
   *
   * Granted without checking anything at M2, because there is nothing to check
   * against yet. What matters now is that the *server* decides and the answer
   * comes back on the welcome, so that when M4 puts an audit log and a roster
   * behind it, nothing above this line changes.
   */
  enforcement?: boolean;
  /**
   * Claim the reserved plate and the network with it.
   *
   * Gated by a key the server holds. There is no account system and there will
   * not be one until M4, so this is the whole of it: the server checks a shared
   * secret and decides, exactly as it decides everything else.
   */
  admin?: string;
}

/**
 * An intent, not a position. The client says where it wants to go and the
 * server decides how the vehicle gets there and whether it arrives.
 */
export interface DriveMessage {
  t: 'drive';
  /** Destination node in the county graph. */
  node: number;
}

/**
 * Driving by hand.
 *
 * Sent on change rather than every frame -- there are three states per axis and
 * a key is held for whole seconds, so a message per keypress is a handful per
 * junction rather than sixty a second.
 *
 * The car stays on the graph. §5's model is (edge, offset) and does not bend
 * for this: what the driver controls is how fast, and which road out of the
 * next junction. Free 2D movement would make a plate reader impossible to
 * simulate honestly, and it is the one thing the whole design rests on.
 */
export interface SteerMessage {
  t: 'steer';
  /** 0 to 1. What the accelerator is doing. */
  throttle: number;
  /**
   * The compass direction wanted, in radians, or null for "carry straight on".
   *
   * A heading rather than a turn: W is north and S is south, which is what
   * those keys mean, and it suits a section grid besides -- "keep going north"
   * is a thing a driver thinks and "take the second left" is not. The road
   * network supplies the geometry at each junction.
   */
  want: number | null;
}

/**
 * What this client can currently see of the county.
 *
 * Interest management used to be a fourteen-hop walk of the road graph from
 * wherever the player was. In a section grid fourteen hops is a few blocks, so
 * a player zoomed out to half the county was shown traffic in a small disc
 * around themselves and an empty map everywhere else — which reads as a bug in
 * the simulation rather than as a property of the netcode, and is the first
 * thing anybody notices.
 *
 * A map shows what is on screen. So the client says what is on screen and the
 * server sends the traffic that is in it, nearest first, up to a cap.
 *
 * This does not weaken §5's anti-cheat rule, which is that no field may travel
 * that a route could be reconstructed from. A position on a map is not that: a
 * plate is, and a plate is still gated on the eyesight rule and sent for
 * exactly the handful of cars close enough to read one off. Somebody who lies
 * about their viewport learns where some cars are, which is what standing on a
 * bridge tells you.
 */
export interface ViewMessage {
  t: 'view';
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Choose the PIN for a car that has just been handed over.
 *
 * §4 calls a PIN a car key rather than a password: it unlocks a fictional
 * vehicle and nothing else, and there is nothing about a person behind it. But
 * a key the server invents is a key nobody can remember, which made the
 * browser's copy the only usable copy — so clearing site data lost cars whose
 * registrations were in the database the whole time.
 *
 * One PIN per account, set once, covering every car on the keyring. Four cars
 * with four PINs is four things to lose rather than one thing to remember.
 */
export interface SetPinMessage {
  t: 'setPin';
  pin: string;
  /**
   * The plate to wear, if the player would rather not keep the one issued.
   *
   * Offered once, at registration, and only while the car has never been
   * photographed. That restriction is not a technicality: detections are keyed
   * on the plate string, so a car that could change plates after being read
   * could shed its entire record, and the record is the game.
   *
   * Omitted means keep what was issued.
   */
  plate?: string;
}

/**
 * Put your keys on a device that has none.
 *
 * The code is shown on a device that is already signed in and read off it, so
 * nothing has to be remembered; the PIN is the only thing the player was ever
 * asked to know, and it is what proves the code was not simply found.
 */
export interface PairMessage {
  t: 'pair';
  code: string;
  pin: string;
}

/**
 * Begin a passkey exchange: either saving one, or signing in with one.
 *
 * **The one thing in this game nobody has to remember.** Recovery needs bits
 * from somewhere, and there are only three sources: something you remember,
 * something you hold, or somebody who vouches for you. A four digit PIN is
 * thirteen bits of the first, which is not enough to both find an account and
 * prove it is yours -- and every attempt to squeeze more out of memory failed
 * on the same rock. A plate a machine generated is unmemorable; a plate you
 * chose is public, because every camera in this county reads it on purpose. A
 * place you hid something is not a point in anybody's memory, it is a region.
 *
 * So the bits come from something held, and the least burdensome thing anybody
 * holds is the one their operating system already manages: Face ID, Windows
 * Hello, a password manager. It syncs itself, it cannot be guessed, it cannot
 * be phished, and there is no name, no email and no profile behind it.
 *
 * It does not contradict section 4 either. That section says a PIN is a car
 * key rather than a password. A passkey is more literally a key than the PIN
 * ever was -- it opens this and nothing else, and there is nothing about a
 * person inside it.
 */
export interface PasskeyStartMessage {
  t: 'passkeyStart';
  /** `save` while signed in; `open` from a device holding nothing. */
  how: 'save' | 'open';
}

/** The authenticator's answer, handed straight back for checking. */
export interface PasskeyFinishMessage {
  t: 'passkeyFinish';
  how: 'save' | 'open';
  /** Whatever the browser produced. Shape belongs to WebAuthn, not to us. */
  response: unknown;
}

/**
 * The administrator asking what a selected car is up to.
 *
 * God view, and deliberately admin-only: the whole civilian game is that
 * nobody can see intent, and the whole enforcement game is that the network
 * records plates rather than purposes. The county's operator gets the truth
 * for running the county, the same way the ops page does.
 */
export interface InspectMessage {
  t: 'inspect';
  id: number;
}

/**
 * The administrator asks for a plate's enforcement record: its stops and
 * pursuits. Oversight only -- the server refuses it to anyone but the admin,
 * because this is the county's accountability seat, not a reader terminal.
 */
export interface OversightMessage {
  t: 'oversight';
  plate: string;
}

/** The administrator asks to ride along with a connected player. */
export interface RideAlongMessage {
  t: 'rideAlong';
  id: number;
}

/** The administrator opening a camera to visit it and see what it is capturing. */
export interface CameraDetailMessage {
  t: 'cameraDetail';
  id: string;
}

/**
 * Ask whether a custom plate is available, and what it costs.
 *
 * A personalized plate is a thing you buy, the way Wisconsin sells them, so
 * before any money moves the player finds out if the string is free and legal.
 */
export interface PlateCheckMessage {
  t: 'plateCheck';
  plate: string;
  /** Two-letter state the plate would be issued in. */
  state: string;
}

/** Buy the custom plate. The reply is a Checkout URL to open. */
export interface PlateBuyMessage {
  t: 'plateBuy';
  plate: string;
  state: string;
}

/** Ask for a code to put these keys on another device. */
export interface PairingMessage {
  t: 'pairing';
}

/**
 * Take a parked car that is not yours.
 *
 * The one civilian action in the game that puts you on the other side of the
 * argument, and the reason it is here. Everything else a civilian does --
 * finding cameras, covering them, filing reports -- is a case for the network
 * being watched. This is the case for it existing: a car goes missing, nobody
 * knows for an hour, and when the owner finally calls it in there is already a
 * record of where it went.
 *
 * Both of those are true at once. A game that only carried the first one would
 * be making a weaker argument than the honest one.
 *
 * No plate: whatever is parked nearest, within STEAL_METERS. Choosing a
 * specific car by plate would mean a civilian could pick a plate out of thin
 * air, and nothing in this protocol lets a route travel in a field.
 */
export interface StealMessage {
  t: 'steal';
}

export interface StopMessage {
  t: 'stop';
}

export interface PingMessage {
  t: 'ping';
  id: number;
}

/**
 * An enforcement search.
 *
 * DESIGN.md section 2: you have the network and you cannot see anyone with it.
 * You have plate reads with timestamps, and you build a theory out of them.
 * This is the whole of what that side does.
 *
 * Note what is not on it: any reason for asking. That absence is the point, and
 * at M4 it becomes the audit log and the unjustified search.
 */
export interface QueryMessage {
  t: 'query';
  /** A plate to follow, or a camera to look through. */
  by: 'plate' | 'camera';
  term: string;
  /** World seconds. Omit for everything held. */
  since?: number;
  until?: number;
  /**
   * Why this search is being run, in the searcher's own words.
   *
   * Optional, and it stays optional. The server records whatever arrives,
   * including nothing, and refuses no search for the want of one. See audit.ts:
   * a game that graded reasons would have invented an oversight mechanism the
   * real product does not have, and that absence is the subject.
   */
  reason?: string;
}

/**
 * Drive somewhere, optionally avoiding roads you would rather not be seen on.
 *
 * `avoid` is a list of edge ids the client wants routed around. It cannot be
 * used to cheat, because a client only knows the edges it has discovered by
 * driving them: you can only route around what you found. The server still
 * routes, still decides, and still charges the detour in distance.
 */
export interface AvoidMessage {
  t: 'avoid';
  edges: number[];
}

/**
 * Put something over a camera's lens.
 *
 * Section 8's cheapest interference tier. The server checks you are actually
 * standing next to it, because this is a physical act.
 */
export interface ObstructMessage {
  t: 'obstruct';
  camera: string;
  /**
   * Which tier. Defaults to the cheapest, because the cheapest is the one the
   * design wants players reaching for first.
   */
  how?: 'obstruct' | 'damage' | 'take' | 'destroy';
  /**
   * What it was covered with, when the tier is `obstruct`.
   *
   * Ignored for the heavier tiers, which are not covering anything.
   */
  with?: 'balloons' | 'bag' | 'tape' | 'paint';
}

/**
 * Run at up to twice the posted limit, or stop doing that.
 *
 * Administrators only, checked server-side. The speed goes in the record like
 * anybody else's.
 */
export interface RunMessage {
  t: 'run';
  fast: boolean;
}

/**
 * File what you have found, and ask for what everybody else has.
 *
 * `share` carries camera ids this player has personally driven past. The server
 * checks each one against what that player was actually near, because a client
 * that could report anything could map the county without leaving the driveway.
 */
export interface ReportMessage {
  t: 'report';
  share?: string[];
}

/**
 * Turn the shared network on or off.
 *
 * One boolean, and the largest number on the board behind it. CALIBRATION.md:
 * the median Wisconsin agency shares with 206 other agencies and the upper
 * quartile with 1,178, so a village's nine cameras become a view of everything.
 * Nobody outside the building is asked, and the setting leaves no trace anywhere
 * a member of the public could look.
 */
export interface ShareMessage {
  t: 'share';
  on: boolean;
}

/**
 * Ask for another car.
 *
 * There are no accounts in this game, only car keys, so owning several vehicles
 * is a keyring rather than a profile. That is not a simplification: it is the
 * shape the design already committed to in section 4, and it happens to be
 * exactly what makes the evasion interesting. Your pattern is attached to a
 * plate, so a second plate is a second life, and the reconstruction the network
 * builds of you has a hole in it the size of whatever you did in the other car.
 */
export interface NewVehicleMessage {
  t: 'newVehicle';
}

/**
 * Ask what your agency has had to send somebody out for, or say you looked.
 *
 * With no camera, this asks for the list. With one, it says an officer is
 * standing at it -- the server checks the distance, because establishing why a
 * camera is dark is a thing somebody does with their eyes.
 */
export interface DispatchMessage {
  t: 'dispatch';
  camera?: string;
}

/**
 * Ask what work is going, take a job, or hand one in.
 *
 * With no `job`, this asks for the board. With one and `action: 'take'`, it
 * accepts. With `action: 'finish'`, it says the driving is done -- the server
 * checks whether the car is actually at the drop, because the whole value of a
 * job is the driving it made somebody do.
 */
export interface JobMessage {
  t: 'job';
  /**
   * `arrive` means "I am here, do whatever this leg needs" -- collect on the
   * first leg, deliver on the second. The server decides which, because it is
   * the one that knows where the car actually is.
   */
  action?: 'take' | 'arrive' | 'abandon';
  job?: string;
}

/** Spend money: renew the tag, buy a second car, or fill the tank. */
export interface BuyMessage {
  t: 'buy';
  what: 'registration' | 'vehicle' | 'fuel' | 'scanner' | 'radar' | 'garage';
}

/**
 * Pick a side, or change sides.
 *
 * The game has two halves and showing both at once shows neither. A player is
 * either driving a car through a network that watches them, or sitting at the
 * console doing the watching, and the whole point is what each looks like from
 * the inside. Handing somebody every panel at once collapses that into a
 * dashboard.
 *
 * Switching is free and reversible, because this is a game about understanding
 * both sides rather than a career ladder. The server still decides.
 */
export interface SideMessage {
  t: 'side';
  want: 'civilian' | 'enforcement';
}

/** Ask what the record holds about your own plate. */
export interface WeekMessage {
  t: 'week';
}
/** An officer asking for their standing: rank, department tier, and budget. */
export interface StandingMessage {
  t: 'standing';
}

/** Ask for the searches this unit has run. */
export interface AuditMessage {
  t: 'audit';
  /** How many to return, newest first. Server clamps it. */
  limit?: number;
}

/** Put a plate on the hotlist, take one off, or ask what is on it. */
export interface HotlistMessage {
  t: 'hotlist';
  action: 'add' | 'clear' | 'list';
  plate?: string;
  reason?: string;
  note?: string;
}

/**
 * Pull a vehicle over.
 *
 * The moment the whole thing turns into a person. Everything before it is
 * records; this is a stop, and whether it was a good one depends on what is
 * actually painted on the car rather than on what the camera reported.
 */
export interface PullOverMessage {
  t: 'pullover';
  /** The vehicle to stop. Must be one this client can currently see. */
  id: number;
}

/**
 * Light somebody up.
 *
 * The other half of enforcement on the street: not a plate you already have up
 * close, but a car you can see down the road and have decided to chase. The
 * server checks you can actually see it (a long line-of-sight, not the narrow
 * plate-read corridor) and then hands the officer to the same pursuit machinery
 * that runs the automated speeding chases -- lights on, chase speed, and a
 * cruiser that closes the gap on its own until the driver takes the wheel.
 */
export interface PursueMessage {
  t: 'pursue';
  /** The vehicle to chase. Must be one this officer can currently see. */
  id: number;
}

/** Break off the pursuit this officer is running. */
export interface BreakOffMessage {
  t: 'breakoff';
}

/**
 * Send a unit after a plate.
 *
 * The other half of dispatch: not a dark camera to go look at, but a suspect to
 * go get. The server only allows it on intel -- the officer can currently see
 * the car, or a Flock camera read it recently -- and then routes the nearest
 * free NPC unit at the car's last known place to run it down. No intel, no
 * dispatch: a unit is not sent chasing a plate nobody can locate.
 */
export interface DispatchUnitMessage {
  t: 'dispatchUnit';
  /** The plate to send a unit after. */
  plate: string;
}

export type ClientMessage =
  | HelloMessage
  | DriveMessage
  | SteerMessage
  | SetPinMessage
  | PairMessage
  | PairingMessage
  | PlateCheckMessage
  | PlateBuyMessage
  | InspectMessage
  | OversightMessage
  | HeatmapMessage
  | RideAlongMessage
  | CameraDetailMessage
  | PasskeyStartMessage
  | PasskeyFinishMessage
  | StealMessage
  | ViewMessage
  | StopMessage
  | PingMessage
  | QueryMessage
  | HotlistMessage
  | AuditMessage
  | AvoidMessage
  | ObstructMessage
  | ReportMessage
  | ShareMessage
  | SideMessage
  | WeekMessage
  | StandingMessage
  | JobMessage
  | BuyMessage
  | DispatchMessage
  | NewVehicleMessage
  | RunMessage
  | ResetMessage
  | AdminMessage
  | PullOverMessage
  | PursueMessage
  | BreakOffMessage
  | DispatchUnitMessage;

// -- server to client --------------------------------------------------------

export interface WelcomeMessage {
  t: 'welcome';
  /**
   * How long a day is here, in world seconds.
   *
   * Sent because the client was formatting every timestamp against a hardcoded
   * 86,400 while a development county runs a 7,200-second day, so the day
   * counter on the query console disagreed with the county it was querying.
   * The archive's day is the county's day; it is not a constant the client gets
   * to assume.
   */
  daySeconds: number;
  version: number;
  tickHz: number;
  county: { name: string; state: { abbr: string; name: string } };
  /**
   * Whether this server sells custom plates, decided by whether it has a
   * Stripe key. The garage shows its custom-plate box only when this is true,
   * so a keyless server never advertises a plate it cannot deliver -- and, more
   * to the point, a server that DOES sell them shows the box on arrival rather
   * than waiting on a check the hidden box could never send.
   */
  platesForSale: boolean;
  /**
   * Whether this client may query the detection table.
   *
   * Decided by the server. A client that granted itself the network would have
   * the whole game, so this arrives rather than being asserted.
   */
  enforcement: boolean;
  /**
   * The department this officer works for, and the two numbers that make the
   * sharing setting mean something.
   *
   * A median Wisconsin agency runs nine cameras out of a county that holds
   * hundreds. Telling the player both figures up front is what makes the
   * sharing toggle legible as a policy rather than a convenience.
   */
  agency?: string;
  agencyCameras?: number;
  countyCameras?: number;
  you: {
    id: number;
    plate: Plate;
    /**
     * Shown once so it can be written down, and stored by the client. Only
     * present when the vehicle was just issued.
     */
    pin?: string;
  };
  /**
   * Every other plate that opens with the key you just used.
   *
   * The keyring, out of the database. It used to exist only in localStorage,
   * which meant clearing site data lost cars whose registrations were in
   * Postgres the whole time -- and the plate is the identity in this game, so
   * losing the plate loses the car.
   *
   * This does not weaken the thing localStorage was protecting. Nothing in the
   * *network* can ask which plates belong together: there is no field on a
   * detection, a search result or an audit line that could answer it. This is
   * the registry answering a question you proved you may ask, by knowing the
   * key, and it is the same question a garage answers when you walk in with
   * one.
   *
   * Absent when there is only the car you are in.
   */
  keyring?: string[];
  /**
   * This car has no key yet and the player has to choose one.
   *
   * Set when somebody arrives with no key at all and is handed a car. Until a
   * PIN is chosen the registration does not exist, so nothing can reclaim the
   * car -- including its own driver, which is exactly why they are asked.
   */
  needsPin?: boolean;
  /** Something the player should be told about how they got this vehicle. */
  note?: string;
}

/**
 * Everything a client is entitled to know about its own vehicle.
 *
 * This is the one entity a client gets the route for, because it is the one
 * entity it is allowed to predict.
 */
export interface SelfState {
  /** Your own lights, so you hear your own siren like everybody else does. */
  lights?: boolean;
  edge: number;
  forward: boolean;
  offset: number;
  speed: number;
  atNode: number;
  /**
   * How far into the route **as this client was last sent it**.
   *
   * Not the server's own index. The server sends the remaining tail of a
   * route, so its index counts edges the client never received and the two
   * numbers would not be comparable. Rebasing here is what lets a client that
   * fell behind, by being in a background tab or on a bad link, snap back onto
   * the right edge instead of predicting from a stale one forever.
   */
  routeIndex: number;
  destination: number;
  /**
   * This driver's own speed relative to the posted limit.
   *
   * Sent so the client predicts with the number the server is actually using
   * -- the mirror vehicle used to invent its own, so prediction ran at a
   * different speed than the truth and reconciliation kept snapping it back.
   * It is also what makes the ETA honest: a route's drive time at 2x is half
   * the free-flow figure, and the panel was quoting free-flow.
   */
  speedMultiplier: number;
  /**
   * Litres in the tank.
   *
   * On the snapshot rather than on the job board, because it changes every tick
   * and a gauge that updated when somebody happened to open a panel would be a
   * gauge nobody trusted. It is your own car; nothing about anybody else's
   * fuel is ever sent.
   */
  fuel: number;
}

/**
 * What one client is told about somebody else's vehicle when it is close
 * enough to read the plate off it.
 *
 * A drive-by, in other words. You get what a person gets leaning over to look:
 * the plate, roughly how fast they are going, and what road they are on. You
 * do not get where they are going, because you cannot see that from a car and
 * the game is about the difference.
 *
 * The server decides whether to attach this; see canReadPlate in vision.ts. A
 * client is never sent it and told to be honest about it.
 */
export interface PlateRead {
  /** The characters on the plate. */
  p: string;
  /** Issuing jurisdiction code, which the client resolves to a name. */
  j: string;
  /** Meters per second. */
  s: number;
  /** Index into the county's roadNames, or -1 for an unnamed road. */
  r: number;
  /** Index into ROAD_CLASSES. */
  c: number;
  /**
   * Registration expired.
   *
   * Sent with the plate because it *is* the plate: the month and year are on a
   * sticker in public view, and a system checking a serial against state
   * registration data knows this on a perfect read with no hotlist involved.
   * Anyone entitled to read the plate is entitled to this.
   */
  x?: boolean;
}

/**
 * Everything a client is entitled to know about somebody else's vehicle.
 *
 * Position and heading, always: you can see a car. A plate only when you are
 * near enough to read one, and never a route or a destination. Identifying a
 * vehicle you are not next to is what the camera network is for, and it is the
 * thing the game makes you work for.
 */
export interface EntityState {
  id: number;
  x: number;
  y: number;
  /** Radians, world frame. */
  h: number;
  /**
   * A marked police car, and whether its lights are on.
   *
   * Sent for every vehicle in view regardless of distance, unlike `read`,
   * because it is not a fact about the plate -- it is livery. Anybody can see a
   * squad car from across a car park, and that is the whole difference between
   * what a person notices and what the network records.
   */
  patrol?: boolean;
  lights?: boolean;
  /** Present only when this client is close enough to read the plate. */
  read?: PlateRead;
}

export interface SnapshotMessage {
  t: 'snapshot';
  tick: number;
  /** Sim seconds, so a client can label a read with the time it happened. */
  time: number;
  you: SelfState;
  /** Sent only when it changed, because it rarely does. */
  route?: number[];
  entities: EntityState[];
}

/** One row out of the detection table. */
export interface DetectionRow {
  /** The plate that was read. */
  p: string;
  /**
   * The state that issued it.
   *
   * A search matches on the serial alone, because six characters is what the
   * person typing has. Two states issue the same serial to different cars, so
   * one answer can hold two lives, and this is the column that says which is
   * which. It is sent for every row, always, and nothing makes anybody read it.
   */
  j: string;
  /** Which camera read it. */
  c: string;
  /** The junction that camera guards. */
  n: number;
  /** Compass bearing of travel at the moment of the read. */
  b: number;
  /** Meters per second. */
  s: number;
  /** World seconds. */
  at: number;
}

/**
 * One incident out of the enforcement record: a stop or a pursuit.
 *
 * Reads say where a plate was photographed. This says what was *done* about it
 * -- a marked unit lighting up, a citation, a traffic stop and how it went --
 * because a chase that leaves no trace a search can find is the exact gap a
 * player hit when a cruiser lit them up and the console had nothing on it.
 */
export interface IncidentRow {
  /** What kind of event: 'pursuit' or 'stop'. */
  kind: string;
  /** The short outcome word: pursued, cited, eluded, clear, hotlist, wrongful, tag. */
  outcome: string;
  /** A human line describing what happened. */
  detail: string;
  /** The unit or officer plate involved. */
  unit: string;
  /** World seconds. */
  at: number;
}

export interface QueryResultMessage {
  t: 'result';
  by: 'plate' | 'camera';
  term: string;
  rows: DetectionRow[];
  /**
   * The enforcement record for this plate: stops and pursuits, newest first.
   * Present on a plate search; empty or absent on a camera search, which is
   * asking a different question.
   */
  incidents?: IncidentRow[];
  /** True when the answer was cut off, so a player knows they are not seeing all of it. */
  truncated: boolean;
  /** How long the search took, because a real console shows you. */
  millis: number;
}

/**
 * A hotlist plate just passed a camera.
 *
 * DESIGN.md section 1: the alert says *seen here, this second*, with the
 * camera's position. One second later the vehicle is somewhere in a widening
 * circle of road. What it never carries is where the car is now, because the
 * network does not know.
 */
export interface AlertMessage {
  t: 'alert';
  /** What the camera reported reading. Not necessarily what was there. */
  plate: string;
  /** Which camera, and the junction it guards. */
  camera: string;
  node: number;
  bearing: number;
  speed: number;
  at: number;
  /** Why that plate is on the list. */
  reason: string;
  note: string;
  /**
   * The state that issued the plate on the wanted vehicle, and what that
   * vehicle is.
   *
   * Aurora, 2020: a Colorado plate matched a stolen motorcycle on Montana
   * plates, and a mother and four children ended up face down on the pavement
   * at gunpoint. The reporting is specific that the officers did not cross
   * reference the state or the make. Both were in the record.
   *
   * So both are here, on every alert, and nothing in the game makes anybody
   * read them.
   */
  jurisdiction: string;
  wanted: string;
  /** How long the entry has been on the list, in world seconds. */
  age: number;
}

/** One row of the hotlist, as enforcement sees it. */
export interface HotlistRow {
  plate: string;
  reason: string;
  note: string;
  addedAt: number;
  /** True when the reason stopped being true and nobody has cleared it. */
  stale: boolean;
}

export interface HotlistStateMessage {
  t: 'hotlistState';
  entries: HotlistRow[];
}

/**
 * What happened when a vehicle was pulled over.
 *
 * `wrongful` is decided against the plate the car actually carries, not the
 * one the camera reported and not the one the officer believed. That is the
 * only definition that means anything.
 */
export interface StopResultMessage {
  t: 'stopResult';
  plate: string;
  /** True when this car really is on the hotlist. */
  onHotlist: boolean;
  /** True when it is on the list for something already over. */
  stale: boolean;
  /**
   * True when the plate matched but the vehicle did not: wrong state, wrong
   * make, wrong body. The Aurora case. Everything needed to avoid it was on
   * the alert.
   */
  mismatched: boolean;
  /** What the record said was wanted, and what was actually stopped. */
  wanted: string;
  found: string;
  /**
   * Registration was expired.
   *
   * A lawful basis for the stop that has nothing to do with any list, and the
   * commonest pretext there is. It makes a stop justified that would otherwise
   * have been wrongful, which is the point: not every stop in this game is the
   * alert stream's fault.
   */
  expired: boolean;
  wrongful: boolean;
  reason: string;
  /** How many wrongful stops this client has now made. */
  wrongfulCount: number;
  stopCount: number;
}

export interface PongMessage {
  t: 'pong';
  id: number;
  tick: number;
}

export interface ErrorMessage {
  t: 'error';
  message: string;
  /**
   * Which part of the game this is about.
   *
   * The client used to put every error in the search console's status line
   * whenever that console was open, so "not close enough to reach that camera"
   * appeared where the result of a plate search goes. An error has a subject and
   * the interface has to be told what it is; guessing from context does not
   * work when the context is "enforcement, doing several things at once".
   *
   * Absent means it belongs to the world rather than to a panel.
   */
  about?: 'query' | 'hotlist' | 'audit' | 'key' | 'steal' | 'pair' | 'passkey' | 'plate';
  /**
   * A credential id this server has never seen, sent back so the browser can
   * delete it from its own password manager.
   *
   * The orphan problem: password managers save a passkey the moment the OS
   * ceremony completes, before the server has verified anything -- and they
   * keep it after the server wipes. Every wiped account left a dead key in
   * the picker, every dead key failed sign-in into a confusing cascade, and
   * nothing on the server could reach into the manager to clean it. Chrome's
   * Signal API can: the client passes this id to signalUnknownCredential and
   * the manager quietly removes the corpse.
   */
  credential?: string;
}

/**
 * The searches this unit has run, newest first.
 *
 * A player can always read their own log. That is not a concession, it is the
 * point: the record is legible, it was always legible, and it still took
 * somebody deciding to look.
 */
export interface AuditLogMessage {
  t: 'auditLog';
  entries: AuditEntry[];
  /** Every search this unit has ever run, not just the ones sent. */
  total: number;
  /** How many of those stated no reason. */
  unexplained: number;
}

export interface AuditEntry {
  /** A search, or a change to what a search can reach. See SearchRecord. */
  by: 'plate' | 'camera' | 'network';
  term: string;
  /** Empty when none was given, which is a value and not a gap. */
  reason: string;
  at: number;
  rows: number;
}

/**
 * A camera you just drove past.
 *
 * DESIGN.md section 8: coverage is never handed to a client, it is learned by
 * driving into it, and every mile spent building that map is a mile the network
 * watched you drive. So this is only ever sent for a camera the player is
 * physically near, and only once.
 *
 * What it carries is what looking at a pole tells you: where it is, which way
 * it faces, and which approach it watches. Not who operates it, not where it
 * came from in the record, and not whether there are others: those are archive
 * facts and the community map is where they belong.
 */
export interface CameraSightingMessage {
  t: 'cameras';
  /**
   * Which kind of knowledge this is, and it matters a great deal.
   *
   * `found` is what this plate has driven past. It is earned, it persists, and
   * §8 is emphatic that it is the one thing the civilian side has to work for.
   *
   * `coverage` is an agency's own deployment, sent because operators know where
   * their cameras are -- the same section says so plainly. It is **not** the
   * same knowledge and must never become it: it arrives on signing on, it is
   * held apart, and it goes when the shift ends. A client that merged the two
   * would hand a civilian a free map of every camera their agency runs, which
   * is precisely the thing discovery exists to prevent.
   */
  scope?: 'found' | 'coverage';
  seen: SightedCamera[];
}

export interface SightedCamera {
  id: string;
  x: number;
  y: number;
  /**
   * Which way it looks, in compass degrees.
   *
   * Computed from the approach it watches rather than taken from the record.
   * The archive gives a bearing for about two thirds of these and -1 for the
   * rest, and a client drawing -1 points every one of them due north -- which
   * put a camera on an east-west road watching a field. The server knows the
   * edge and the direction of travel, so it knows where the camera faces:
   * **along** that traffic, not against it, because a plate reader shoots the
   * back of a car that has already passed.
   */
  b: number;
  /** The approach it watches: edge id, and whether it reads A-to-B traffic. */
  e: number;
  f: boolean;
  /**
   * Not reading right now.
   *
   * A bag over the lens looks like this from the road, and it looks like
   * nothing at all from the console. See section 8.
   */
  down?: boolean;
  /**
   * What is over the lens, when something is.
   *
   * Sent so the map can show it. A camera with a bag on it and a camera with a
   * dead backhaul are identical from the console -- section 8 is emphatic about
   * that -- but they are not identical from the pavement, and this map is what
   * somebody has actually seen.
   */
  downWith?: 'balloons' | 'bag' | 'tape' | 'paint';
}

/**
 * Something happened that the player should know about, and it is not a fault.
 *
 * Split out because success was being reported through the error channel, which
 * works and is a lie: a covered camera and a refused search would arrive as the
 * same kind of thing, and the interface has no way to tell them apart.
 */
export interface NoticeMessage {
  t: 'notice';
  message: string;
  /**
   * The speed multiplier now in force on this client's own vehicle.
   *
   * Sent when it changes, because the client predicts its own movement and a
   * client predicting at the wrong speed would fight the server every tick.
   */
  speedMultiplier?: number;
  /**
   * Whether the shared network is on, as the server has it.
   *
   * Sent rather than assumed because the console's scope line is the only thing
   * that says whose cameras a search just went through, and a client tracking
   * that with its own boolean will eventually be showing one number while the
   * server uses another. The scope of a search is not something to guess at.
   */
  sharing?: boolean;
}

/**
 * The map players have built for each other.
 *
 * Not a view of the world. A view of what people said, which is incomplete,
 * stale and biased toward the roads people drive -- the same bias the real
 * archive has, arrived at the same way.
 */
export interface CommunityMapMessage {
  t: 'communityMap';
  markers: CommunityMarker[];
  /** How many of these this player contributed. */
  yours: number;
}

export interface CommunityMarker {
  x: number;
  y: number;
  b: number;
  /** How many different people said it is here. */
  n: number;
  /** World seconds of the most recent report. */
  seen: number;
  /**
   * The archive's own confidence vocabulary, which is borrowed rather than
   * invented: deciding whether to trust somebody else's marker is the same
   * judgement the archive makes about a sighting versus a permit.
   */
  c: 'unconfirmed' | 'corroborated' | 'persistent' | 'field-verified';
}

/**
 * What this agency has to send somebody out for, and what it has spent.
 *
 * Section 8: attention is finite and vandalism competes for it against
 * everything else. An hour spent on somebody who covered a lens is an hour not
 * spent on the case the purchase was justified by.
 *
 * The ledger reports and does not grade, the same way the audit log records and
 * does nothing. Putting a number on the screen is the whole intervention.
 */
export interface DispatchBoardMessage {
  t: 'dispatchBoard';
  agency: string;
  open: OpenDispatch[];
  ledger: {
    alerts: number;
    searches: number;
    stops: number;
    wrongfulStops: number;
    dispatches: number;
    dispatchesResolved: number;
    casesClosed: number;
    /** Fines and recoveries. Reported, never used to decide a renewal. */
    revenue: number;
  };
}

export interface OpenDispatch {
  camera: string;
  x: number;
  y: number;
  /** World seconds since anybody noticed it had gone quiet. */
  raised: number;
}

/**
 * The work that is going, and what this player has.
 *
 * Money is here rather than in the welcome because it changes constantly, and
 * the two things it buys -- a second plate, and a current tag -- are the two
 * places the rest of the game charges a real price.
 */
export interface JobBoardMessage {
  t: 'jobBoard';
  jobs: OfferedJob[];
  /** The one in progress, if any. */
  current: OfferedJob | null;
  /**
   * Which leg of it. A job is two drives -- go to where the thing is, then take
   * it where it is going -- so where somebody is headed depends on this.
   */
  stage?: 'collect' | 'deliver';
  /** Where they are headed right now, which is one end or the other. */
  target?: { node: number; name: string; town: string; kind: string; x: number; y: number };
  money: number;
  /** What this player has earned and how, because the split is the point. */
  earnedLegal: number;
  earnedIllegal: number;
  /** Litres in the tank, and what it holds. */
  fuel: number;
  tank: number;
  /** The nearest filling station, when there is one worth naming. */
  nearestFuel?: { name: string; brand: string; node: number; away: number };
  /** Whether this account owns a scanner, so its app knows what to show. */
  scanner?: boolean;
  /** Whether this patrol car has a radar gun fitted. */
  radar?: boolean;
  /** The department's spendable revenue, for enforcement kit like the radar gun. */
  agencyRevenue?: number;
  /** The officer's rank index, so the action bar can gate tools by rank. */
  rank?: number;
  /** The department's operational budget, spent on call-outs and kit. */
  budget?: number;
  /** How many units the department may have out at once, and how many are now. */
  dispatchCap?: number;
  dispatchActive?: number;
  /** How many cars this account's garage holds. */
  garageCapacity?: number;
  /** How many cars the account currently owns. */
  carsOwned?: number;
  /** True when a completed job put this plate on a list. */
  wanted: boolean;
}

export interface OfferedJob {
  id: string;
  kind: string;
  from: number;
  to: number;
  fromName: string;
  toName: string;
  pays: number;
  illegal: boolean;
  description: string;
  /**
   * The actual places, so the map can draw a pickup and a drop as specific
   * things rather than two circles. A town is not somewhere you deliver to.
   */
  fromVenue: { name: string; kind: string; x: number; y: number };
  toVenue: { name: string; kind: string; x: number; y: number };
}

/**
 * Where the data in this world came from, and when.
 *
 * Section 8's third layer. The archive snapshot is dated and stays dated: after
 * a while nothing will be where the record says, and the record will still say
 * it, because a record is a record.
 */
export interface CountySources {
  county: string;
  /** The date the camera snapshot was taken. */
  fetched: string;
  cameraCount: number;
  /** How many positions came from each kind of source. */
  bySource: Record<string, number>;
  /** And how well established each was, in the archive's own vocabulary. */
  byConfidence: Record<string, number>;
}

/**
 * The plates on this keyring, sent when the set changes.
 *
 * The welcome carries it on arrival; this is for afterwards -- registering a
 * car, or buying one. The client holds keys in the browser for convenience, but
 * the registry is the record, and a browser that has lost its copy gets it back
 * from here.
 */
export interface KeyringMessage {
  t: 'keyring';
  plates: string[];
}

/**
 * The plate this client is now wearing, when it has changed under it.
 *
 * Sent at registration when a car is first named, and again when a custom
 * plate purchase lands from Stripe. `userHandle` rides along on the purchase
 * case: it is the account's WebAuthn user id, base64url, so the client can
 * relabel the saved passkey to the new plate with signalCurrentUserDetails --
 * no new ceremony, because the passkey authenticates the account, not the
 * plate, and the account has not changed.
 */
export interface PlateMessage {
  t: 'plate';
  plate: Plate;
  userHandle?: string;
}

/** Whether a custom plate can be bought, and for how much. */
export interface PlateOfferMessage {
  t: 'plateOffer';
  plate: string;
  ok: boolean;
  reason: string;
  priceCents: number;
}

/** A Stripe Checkout URL to open for the plate purchase. */
export interface PlateCheckoutMessage {
  t: 'plateCheckout';
  url: string;
}

/**
 * What the browser should hand the authenticator.
 *
 * Passed through untouched. These are WebAuthn's own option objects and this
 * game has no business reshaping them.
 */
export interface PasskeyOptionsMessage {
  t: 'passkeyOptions';
  how: 'save' | 'open';
  options: unknown;
}

/** Whether this account has a passkey saved, so the Garage can say so. */
export interface PasskeyStateMessage {
  t: 'passkeyState';
  saved: number;
  /**
   * Present when saving the passkey also registered the car.
   *
   * The one-tap path: an unregistered car whose driver touches the sensor is
   * registered on the spot with a key the server made up. The key comes back
   * so the browser can keep resuming the ordinary way; the durable copy of the
   * identity is the passkey, and this is only the convenience cache.
   */
  plate?: string;
  pin?: string;
}

/** What the administrator sees about one car: the truth, briefly. */
export interface InspectReplyMessage {
  t: 'inspect';
  id: number;
  plate: string;
  kind: string;
  /** Miles per hour, already converted; the admin panel does no arithmetic. */
  mph: number;
  /** The road it is on right now, named, or 'parked'. The operator's truth, not a read. */
  road: string;
  /** Lights and siren running. */
  lights: boolean;
  /** The call a unit is out on, in scanner words, or empty. */
  call: string;
  /** Where it is headed, as a town, or empty for nowhere. */
  headed: string;
  /** On the hotlist right now. */
  wanted: boolean;
  /** Driven by a person, not the simulation. */
  player: boolean;
  /** Recent notable activity, newest first, clock-stamped by the server. */
  recent: string[];
  /**
   * The durable enforcement record for this plate -- the stops and pursuits it
   * has been through, newest first. Only the administrator's inspector is sent
   * it; a working officer's selection never is (the record is the oversight
   * seat's power, not the street's).
   */
  incidents?: IncidentRow[];
  /** World position now, so the operator can pan straight to it. */
  x: number;
  y: number;
}

/** A pairing code and when it stops working, both to be shown. */
export interface PairingCodeMessage {
  t: 'pairingCode';
  code: string;
  /** Sim-independent: seconds from now, so the client can count down. */
  seconds: number;
}

export interface SourcesMessage {
  t: 'sources';
  sources: CountySources;
}

/** The side this player is now on, and what it comes with. */
export interface SideChangedMessage {
  t: 'sideChanged';
  enforcement: boolean;
  agency?: string;
  agencyCameras?: number;
  countyCameras?: number;
  /** World seconds this shift runs out, when on one. */
  shiftEnds?: number;
  /** World seconds before another shift can be signed on for. */
  restEnds?: number;
}

/**
 * The portal, inverted.
 *
 * Every line here is something a transparency portal reports in aggregate,
 * about everybody, in a way nobody can find themselves in. This is the same
 * figures about **one plate** -- which is the thing no portal does, and the
 * reason the last line is always zero.
 */
export interface WeekMessageReply {
  t: 'yourWeek';
  plate: string;
  reads: number;
  cameras: number;
  searches: number;
  searchesUnexplained: number;
  daysSeen: number;
  busiestDay: number;
  /** Alerts this plate raised, and stops it was subjected to. */
  alerts: number;
  stops: number;
  wrongfulStops: number;
  /** How many days of record the window covers. */
  windowDays: number;
}

/**
 * An officer's standing: their own rank, their department's tier and what it
 * unlocks, and the operational budget. Sent when the Standing tab is opened and
 * pushed again whenever a stop changes any of it.
 */
export interface StandingReplyMessage {
  t: 'standingReport';
  agency: string;
  /** The officer's rank and record. */
  rank: string;
  rankIndex: number;
  score: number;
  nextRank: string | null;
  toNextRank: number;
  justified: number;
  wrongful: number;
  pursuits: number;
  /** The department's standing tier and record. */
  tier: string;
  tierIndex: number;
  casesClosed: number;
  nextTier: string | null;
  toNextTier: number;
  /** Units the department may have out on suspects at once. */
  dispatchCap: number;
  /** True when wrongful conduct is holding the department's tier down. */
  heldByConduct: boolean;
  /** Share of the department's stops that were wrongful, 0..1. */
  wrongfulRatio: number;
  /** The operational budget, spent on call-outs and kit; never buys standing. */
  budget: number;
}

/**
 * One point on a plate's reconstructed journey: a camera that read it, where and
 * when. The sequence of these, in time order, is the path the network watched a
 * car take -- the thing an ALPR search is really for, drawn on the map.
 */
export interface JourneyPoint {
  /** The junction the reading camera guards, into the county graph. */
  n: number;
  /** Sim seconds of the read. */
  at: number;
  /** Miles per hour at the read, already converted. */
  mph: number;
  /** Compass bearing of travel at the read. */
  b: number;
}

export interface OversightReplyMessage {
  t: 'oversightResult';
  plate: string;
  incidents: IncidentRow[];
  /**
   * Every camera read of this plate in the retained window, newest first --
   * the journey, for the administrator to trace on the map. The reader terminal
   * an officer uses returns these too; here they are joined to the enforcement
   * record the officer's console is not allowed to hold.
   */
  journey: JourneyPoint[];
}

/** The administrator asks for the traffic heatmap: where and when the network reads. */
export interface HeatmapMessage {
  t: 'heatmap';
}

/** One pole's contribution to the heatmap: where it is and how much it has read. */
export interface HeatCell {
  x: number;
  y: number;
  reads: number;
}

export interface HeatmapReplyMessage {
  t: 'heatmapResult';
  /** Every camera that has read anything, with its read count -- the "where". */
  cells: HeatCell[];
  /** Reads by hour of the county day, 24 bins -- the "when". */
  hours: number[];
  total: number;
}

/** Everything the operator sees riding along with a player: their whole state. */
/** One capture off a camera: a plate it read, with when and how fast. */
export interface CameraCapture {
  plate: string;
  jurisdiction: string;
  mph: number;
  bearing: number;
  at: number;
  /** The vehicle fingerprint the reader also captured. */
  make: string;
  color: string;
  body: string;
}

/** A camera's detail and its recent captures -- an ALPR reader's actual output. */
export interface CameraDetailReplyMessage {
  t: 'cameraDetailResult';
  id: string;
  road: string;
  place: string;
  agency: string;
  category: string;
  bearing: number;
  setback: number;
  reads: number;
  down: boolean;
  coveredWith: string;
  x: number;
  y: number;
  captures: CameraCapture[];
}

export interface RideAlongStateMessage {
  t: 'rideAlongState';
  id: number;
  plate: string;
  side: string;
  agency: string;
  money: number;
  earnedLegal: number;
  earnedIllegal: number;
  jobsDone: number;
  job: { from: string; to: string; illegal: boolean; stage: string; pays: number } | null;
  wanted: boolean;
  pursued: boolean;
  mph: number;
  fuel: number;
  fuelCap: number;
  road: string;
  heading: number;
  x: number;
  y: number;
  recent: string[];
}

/**
 * The pursuit this officer is running, as it starts, updates and ends.
 *
 * The officer's own client already knows where every nearby car is from the
 * snapshot, so this does not repeat the geometry -- it names the quarry and its
 * plate so the pursuit HUD can find it and read the distance off the snapshot
 * each frame. `active: false` is the end of it, whatever ended it: caught,
 * eluded, or broken off.
 */
export interface PursuitMessage {
  t: 'pursuit';
  active: boolean;
  /** The vehicle being chased. */
  target: number;
  /** Its plate, for the HUD. */
  plate: string;
  /**
   * The speed allowance the officer's cruiser now runs at: the pursuit
   * multiplier when the chase starts, and the ordinary allowance restored when
   * it ends. The client applies it to its own prediction so the cruiser it
   * draws runs at chase speed rather than the posted limit -- a cop pursuing at
   * the speed limit is not pursuing.
   */
  speedMultiplier?: number;
  /**
   * The sight state, when it changes mid-chase. 'eyes' = the officer can see the
   * car and the cruiser is closing on its own; 'lost' = sight is broken, the
   * auto-close has handed the wheel back, and the officer is searching off the
   * last heading/place and whatever the cameras read next.
   */
  sight?: 'eyes' | 'lost';
  /** Radians: which way the car was pointed when last seen. For a 'lost' HUD. */
  lastHeading?: number;
  /** Where it was last seen, in words, for a 'lost' HUD. */
  lastPlace?: string;
  /**
   * Why it ended, when `active` is false: 'caught' | 'eluded' | 'off'. Absent
   * while the chase is live.
   */
  ended?: 'caught' | 'eluded' | 'off';
}

export type ServerMessage =
  | WelcomeMessage
  | WeekMessageReply
  | StandingReplyMessage
  | SideChangedMessage
  | KeyringMessage
  | PairingCodeMessage
  | InspectReplyMessage
  | OversightReplyMessage
  | HeatmapReplyMessage
  | RideAlongStateMessage
  | CameraDetailReplyMessage
  | PasskeyOptionsMessage
  | PasskeyStateMessage
  | PlateMessage
  | PlateOfferMessage
  | PlateCheckoutMessage
  | SourcesMessage
  | ScannerMessage
  | ResetDoneMessage
  | AdminStateMessage
  | AdminEventMessage
  | NoticeMessage
  | JobBoardMessage
  | CommunityMapMessage
  | DispatchBoardMessage
  | AuditLogMessage
  | CameraSightingMessage
  | SnapshotMessage
  | QueryResultMessage
  | AlertMessage
  | HotlistStateMessage
  | StopResultMessage
  | PursuitMessage
  | PongMessage
  | ErrorMessage;

// -- the swappable part ------------------------------------------------------

/**
 * Encode and decode live here alone so the format can change once.
 *
 * When JSON starts to hurt, the binary layout replaces the two function bodies
 * below and nothing else in the project knows. What must not happen is a call
 * site somewhere deciding to serialize a message itself.
 */
export function encode(message: ClientMessage | ServerMessage): string {
  return JSON.stringify(message);
}

export function decode<T extends ClientMessage | ServerMessage>(data: string): T {
  return JSON.parse(data) as T;
}

/**
 * Parse a message that arrived over a socket, returning null rather than
 * throwing. Anything on a socket is hostile input until it parses.
 */
export function tryDecode<T extends ClientMessage | ServerMessage>(data: unknown): T | null {
  if (typeof data !== 'string') return null;
  if (data.length > 64 * 1024) return null;
  try {
    const message = JSON.parse(data) as T;
    if (!message || typeof message !== 'object') return null;
    if (typeof (message as { t?: unknown }).t !== 'string') return null;
    return message;
  } catch {
    return null;
  }
}

/**
 * The three resets, and why they are three.
 *
 * A running county accumulates two different kinds of history and they are
 * nobody's business to erase together. The **world** is what happened: where
 * the cameras ended up after a year of contracts, what the record holds, which
 * searches were run. The **players** are who was there: their plates, their
 * money, the cars they saved for. Wiping one has never been a reason to wipe
 * the other, and a single "reset" button that did both would be a button
 * nobody could safely press.
 *
 * - `cameras` puts the deployment back where the archive said it was, and
 *   changes nothing else. This is the one for after a testing session left the
 *   network somewhere silly.
 * - `world` is a fresh county: cameras back, the clock back to day one, the
 *   detection record, the audit log, the hotlist and the community map all
 *   emptied. **Players keep their cars and their money**, because they did not
 *   ask for a new world.
 * - `players` is the other half: every plate, key and balance gone. The county
 *   is untouched.
 *
 * Each one is named in full by the client and confirmed by the server, because
 * an enum with three values and no confirmation is one mis-click from a year of
 * somebody's play.
 */
export interface ResetMessage {
  t: 'reset';
  what: 'cameras' | 'world' | 'players';
  /** The administrator's key, checked again here rather than trusted from the session. */
  admin: string;
}

/** What a reset actually did, said plainly enough to be worth reading. */
export interface ResetDoneMessage {
  t: 'resetDone';
  what: 'cameras' | 'world' | 'players';
  said: string;
}

/** Ask what an administrator can see: counts, and what a reset would touch. */
/**
 * Something a scanner picked up.
 *
 * Sent only to clients that bought one and are close enough to a unit that is
 * out on the call. §4's line between what a person can perceive and what the
 * network records has a third thing on it now: a public radio channel, which
 * is neither. It is how anybody outside a department finds out what a plate
 * read turned into, and it is available to anybody who buys a receiver — which
 * is exactly the situation on the ground.
 */
export interface ScannerMessage {
  t: 'scanner';
  kind: string;
  said: string;
  /** Roughly where, by the nearest named place. Never a coordinate. */
  where: string;
  at: number;
}

export interface AdminMessage {
  t: 'admin';
  admin: string;
}

/** One camera, with how much it has seen. */
export interface CameraStat {
  id: string;
  road: string;
  agency: string;
  reads: number;
  down: boolean;
  /** police | sheriff | vendor | private | unattributed */
  category: string;
}

/** One agency's whole footprint and record, for the leaderboard. */
export interface AgencyStat {
  name: string;
  /** police | sheriff | vendor | private | unattributed */
  category: string;
  cameras: number;
  down: number;
  searches: number;
  stops: number;
  wrongful: number;
  revenue: number;
  casesClosed: number;
}

/** One search, for the accountability list. */
export interface SearchStat {
  unit: string;
  term: string;
  reason: string;
  at: number;
}

/** A camera that is not reading, and what is over its lens. */
export interface DownCamera {
  road: string;
  agency: string;
  covered: string;
}

/** One live hotlist entry, for the oversight list. */
export interface HotlistEntryView {
  plate: string;
  reason: string;
  note: string;
  jurisdiction: string;
  ageHours: number;
  resolved: boolean;
}

/** A plate the network has reacted to, ranked. */
export interface PlateStat {
  plate: string;
  alerts: number;
  stops: number;
  wrongful: number;
}

/** A player who is connected right now, for the ride-along list. */
export interface OnlineUser {
  id: number;
  plate: string;
  /** civilian | authority | admin */
  side: string;
  road: string;
  /** A short line on what they are doing right now. */
  activity: string;
  wanted: boolean;
}

/** The county, measured. Everything the oversight dashboard counts. */
export interface AdminAnalytics {
  readsTotal: number;
  readsLastHour: number;
  misreads: number;
  alerts: number;
  falseAlerts: number;
  staleAlerts: number;
  stops: number;
  wrongfulStops: number;
  stopsOnNothing: number;
  pursuits: { pursued: number; cited: number; eluded: number };
  citations: number;
  /** Total authority revenue: player officers + NPC units combined. */
  revenue: number;
  /** The player-officer share of that revenue (stops + pursuits). */
  revenuePlayer: number;
  /** The NPC-unit share (patrol citations, speeder stops, recoveries). */
  revenueNpc: number;
  searchesTotal: number;
  searchesUnexplained: number;
  hotlistActive: number;
  hotlistStale: number;
  camerasLive: number;
  camerasDown: number;
  carsOnRoad: number;
  /** Camera ownership split: who the record says runs the readers. */
  categories: {
    police: number;
    sheriff: number;
    vendor: number;
    private: number;
    unattributed: number;
  };
  onlineUsers: OnlineUser[];
  /** Reads per bucket over the recent window, oldest first, for the sparkline. */
  readsSeries: number[];
  downCameras: DownCamera[];
  hotlist: HotlistEntryView[];
  topPlates: PlateStat[];
  work: {
    jobsDone: number;
    earnedLegal: number;
    earnedIllegal: number;
    offered: number;
    offeredIllegal: number;
    inProgress: number;
    inProgressIllegal: number;
  };
  topCameras: CameraStat[];
  agencies: AgencyStat[];
  recentSearches: SearchStat[];
}

/**
 * One thing that just happened in the county, pushed to the administrator live.
 *
 * The oversight feed the officer's console cannot have: full plates, the pole,
 * every kind of event -- reads, hotlist hits, stops, pursuits -- as they land.
 */
export interface AdminEventMessage {
  t: 'adminEvent';
  kind: 'read' | 'alert' | 'stop' | 'pursuit' | 'dispatch';
  /** The vehicle this happened to, so the feed row can be visited. */
  id: number;
  plate: string;
  place: string;
  detail: string;
  /** A short flag that colors the row: hit, wrongful, cited, eluded, pursued. */
  flag: string;
  at: number;
}

export interface AdminStateMessage {
  t: 'adminState';
  cameras: number;
  camerasMoved: number;
  camerasDown: number;
  agencies: number;
  players: number;
  accounts: number;
  detections: number;
  searches: number;
  day: number;
  /** Whether the store that makes any of this durable actually opened. */
  durable: boolean;
  /** The measured county, for the oversight dashboard. */
  analytics?: AdminAnalytics;
  /** The recent live feed, to seed the dashboard on open. */
  feed?: AdminEventMessage[];
}
